import { useMemo, useRef, useState } from "react";
import {
    DndContext,
    DragOverlay,
    PointerSensor,
    useSensor,
    useSensors,
    useDraggable,
    useDroppable,
    closestCorners,
} from "@dnd-kit/core";
import ScoreBadge from "@/components/ScoreBadge";
import AvailabilityPill from "@/components/candidate/AvailabilityPill";
import CandidateVoteBar from "@/components/CandidateVoteBar";
import CandidateHoverCard from "@/components/CandidateHoverCard";
import TrackButton from "@/components/TrackButton";
import ResumePreviewButton from "@/components/ResumePreviewButton";
import { AlertTriangle, GripVertical, MessageSquare, Eye, Search as SearchIcon, X, RefreshCw } from "lucide-react";
import { budgetBadgeClass, budgetLabel } from "@/lib/budget";

function stageAgeDays(app) {
    const iso = app.stage_entered_at || app.updated_at || app.created_at;
    if (!iso) return 0;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return 0;
    return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
}

function ApplicantCard({ app, onClick, onPreviewResume, onPreviewProfile, onRetryParse, onHoverStart, onHoverEnd, agingHeat, selectable, selected, onToggleSelect }) {
    const c = app.candidate || {};
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: app.id,
        data: { app },
    });
    const age = agingHeat ? stageAgeDays(app) : 0;
    const heatCls = agingHeat && age >= 10 ? "border-l-4 border-l-rose-500" : agingHeat && age >= 5 ? "border-l-4 border-l-amber-400" : "";
    return (
        <div
            ref={setNodeRef}
            data-testid={`kanban-card-${app.id}`}
            className={`group bg-white border border-border p-3 mb-2 cursor-grab active:cursor-grabbing select-none transition-all ${heatCls} ${selected ? "ring-2 ring-ikb" : ""} ${isDragging ? "opacity-30" : "hover:border-ikb hover:shadow-sm"}`}
            {...attributes}
            {...listeners}
            onMouseEnter={onHoverStart ? (e) => onHoverStart(app, e.currentTarget.getBoundingClientRect()) : undefined}
            onMouseLeave={onHoverEnd}
            onPointerDown={(e) => {
                listeners?.onPointerDown?.(e);
            }}
            onClick={(e) => {
                e.stopPropagation();
                onClick?.(app);
            }}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                    {selectable && (
                        <input
                            type="checkbox"
                            checked={!!selected}
                            onChange={() => onToggleSelect?.(app.id)}
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                            className={`w-3.5 h-3.5 shrink-0 accent-[#002FA7] ${selected ? "" : "opacity-30 group-hover:opacity-100"} transition-opacity`}
                            data-testid={`kanban-select-${app.id}`}
                        />
                    )}
                    <GripVertical className="w-3.5 h-3.5 text-foreground/40 shrink-0" />
                    <div className="min-w-0">
                        <div className="font-medium text-sm truncate">
                            {c.name || "Unknown"}
                            {agingHeat && age >= 5 && (
                                <span className={`ml-1.5 text-[9px] font-mono px-1 py-px border align-middle ${age >= 10 ? "border-rose-300 bg-rose-50 text-rose-700" : "border-amber-300 bg-amber-50 text-amber-700"}`} title={`${age} days in this stage`} data-testid={`kanban-age-${app.id}`}>
                                    {age}d
                                </span>
                            )}
                        </div>
                        <div className="text-[11px] text-foreground/80 truncate mt-0.5">
                            {c.current_title || "—"}{c.current_company ? ` · ${c.current_company}` : ""}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                    <ScoreBadge
                        score={app.match_score || 0}
                        size="sm"
                        processing={app.scoring_status === "processing" || c.processing_status === "processing"}
                        status={app.scoring_status === "ai_unavailable" || c.processing_status === "ai_unavailable" ? "ai_unavailable" : null}
                    />
                    <div className="flex items-center gap-1" onPointerDown={(e) => e.stopPropagation()}>
                        <ResumePreviewButton
                            candidate={c}
                            testId={`kanban-preview-${app.id}`}
                        />
                        <TrackButton
                            candidateId={c.id || app.candidate_id}
                            variant="icon"
                            testId={`kanban-track-${app.id}`}
                        />
                    </div>
                </div>
            </div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
                {(c.processing_status === "processing" || app.scoring_status === "processing") && (
                    <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-amber-300 bg-amber-50 text-amber-800 inline-flex items-center gap-1" data-testid={`kanban-processing-${app.id}`}>
                        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse" /> Processing
                    </span>
                )}
                {(c.processing_status === "ai_unavailable" || app.scoring_status === "ai_unavailable") && (
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onRetryParse?.(app); }}
                        disabled={!onRetryParse}
                        className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 inline-flex items-center gap-1"
                        title={c.parse_error || app.scoring_error || "AI service paused — click to retry"}
                        data-testid={`kanban-ai-paused-${app.id}`}
                    >
                        <RefreshCw className="w-3 h-3" /> AI paused
                    </button>
                )}
                <AvailabilityPill candidate={c} application={app} testIdPrefix={`kanban-avail-${app.id}`} />
                {c.processing_status === "failed" && (
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onRetryParse?.(app); }}
                        disabled={!onRetryParse}
                        className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100 inline-flex items-center gap-1"
                        title={c.parse_error || "Parse failed — click to retry"}
                        data-testid={`kanban-failed-${app.id}`}
                    >
                        <RefreshCw className="w-3 h-3" /> Retry parse
                    </button>
                )}
                {(c.location) && (
                    <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/80">{c.location}</span>
                )}
                {app.budget_status?.status && (
                    <span
                        className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${budgetBadgeClass(app.budget_status.status)}`}
                        title={`${budgetLabel(app.budget_status.status)}${app.budget_status.delta_pct ? ` (${app.budget_status.delta_pct > 0 ? "+" : ""}${app.budget_status.delta_pct}%)` : ""}`}
                        data-testid={`kanban-budget-${app.id}`}
                    >
                        {budgetLabel(app.budget_status.status)}
                    </span>
                )}
                {(app.analysis?.red_flags || []).length > 0 && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-destructive font-mono uppercase tracking-widest">
                        <AlertTriangle className="w-3 h-3" />
                        {app.analysis.red_flags.length}
                    </span>
                )}
                {app.recruiter_rating && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-amber-700 font-mono uppercase tracking-widest">
                        ★ {app.recruiter_rating.avg}/10
                    </span>
                )}
                {onPreviewProfile && c.id && (
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onPreviewProfile(app); }}
                        className="ml-auto text-[10px] font-mono uppercase tracking-widest border border-border bg-ikb text-white hover:bg-[#001f7a] px-1.5 py-0.5 inline-flex items-center gap-1"
                        data-testid={`kanban-card-preview-${app.id}`}
                        title="Preview profile + AI analysis"
                    >
                        <Eye className="w-3 h-3" /> Preview
                    </button>
                )}
            </div>
            {app.id && (
                <div className="mt-2 pt-2 border-t border-border/60" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()} data-testid={`kanban-card-votebar-${app.id}`}>
                    <CandidateVoteBar
                        applicationId={app.id}
                        initial={app.vote_summary ? { counts: app.vote_summary.counts || {}, my_vote: null, voters: [] } : undefined}
                        size="sm"
                    />
                </div>
            )}
        </div>
    );
}

function Column({ stage, apps, onCardClick, onPreviewResume, onPreviewProfile, totalUnfiltered, onRetryParse, onHoverStart, onHoverEnd, agingHeat, selectable, selectedIds, onToggleSelect }) {
    const { setNodeRef, isOver } = useDroppable({ id: stage.key, data: { stage } });
    // Phase 76 — stale-first sort: when agingHeat is on, cards with higher stage-age float to the top
    const ordered = useMemo(() => {
        if (!agingHeat) return apps;
        return [...apps].sort((a, b) => stageAgeDays(b) - stageAgeDays(a));
    }, [apps, agingHeat]);
    return (
        <div
            ref={setNodeRef}
            data-testid={`kanban-col-${stage.key}`}
            className={`flex flex-col min-w-[260px] w-[260px] bg-secondary/40 border border-border transition-colors ${isOver ? "bg-blue-50 border-ikb" : ""}`}
        >
            <div className="p-3 border-b border-border bg-white flex items-center justify-between gap-2 sticky top-0">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="w-2.5 h-2.5 inline-block shrink-0" style={{ backgroundColor: stage.color || "#64748B" }} />
                    <span className="text-[11px] font-mono uppercase tracking-widest truncate">{stage.label}</span>
                </div>
                <span className="text-[11px] font-mono text-foreground/80" data-testid={`kanban-col-count-${stage.key}`}>
                    {apps.length}{typeof totalUnfiltered === "number" && totalUnfiltered !== apps.length ? ` / ${totalUnfiltered}` : ""}
                </span>
            </div>
            <div className="flex-1 overflow-y-auto p-2 max-h-[calc(100vh-18rem)] min-h-[200px]">
                {apps.length === 0 ? (
                    <div className="text-center text-[11px] text-foreground/60 font-mono uppercase tracking-widest py-6">
                        {typeof totalUnfiltered === "number" && totalUnfiltered > 0 ? "0 matches" : "Drop here"}
                    </div>
                ) : ordered.map((a) => (
                    <ApplicantCard key={a.id} app={a} onClick={onCardClick} onPreviewResume={onPreviewResume} onPreviewProfile={onPreviewProfile} onRetryParse={onRetryParse} onHoverStart={onHoverStart} onHoverEnd={onHoverEnd} agingHeat={agingHeat} selectable={selectable} selected={selectedIds?.has(a.id)} onToggleSelect={onToggleSelect} />
                ))}
            </div>
        </div>
    );
}

export default function KanbanBoard({ apps, stages, onCardClick, onStageChange, onPreviewResume, onPreviewProfile, onRetryParse, smartHover = false, onHoverStart, onHoverEnd, agingHeat = false, bulkSelect = false, onBulkAction }) {
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
    const [activeId, setActiveId] = useState(null);
    const [q, setQ] = useState("");
    const [hover, setHover] = useState(null);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [bulkStage, setBulkStage] = useState("");
    const hoverTimerRef = useRef(null);

    const toggleSelect = (id) => setSelectedIds((cur) => {
        const next = new Set(cur);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const clearSelection = () => setSelectedIds(new Set());
    const fireBulk = (action, extra) => {
        onBulkAction?.(action, [...selectedIds], extra);
        clearSelection();
        setBulkStage("");
    };

    const hoverStart = smartHover ? (app, rect) => {
        if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = setTimeout(() => {
            setHover({ app, rect });
            onHoverStart?.(app, rect);
        }, 400);
    } : undefined;

    const hoverEnd = smartHover ? () => {
        if (hoverTimerRef.current) { clearTimeout(hoverTimerRef.current); hoverTimerRef.current = null; }
        setHover(null);
        onHoverEnd?.();
    } : undefined;

    // Filter by free-text query — matches name/email/skills/location/current_title/current_company
    const filtered = useMemo(() => {
        const qs = q.trim().toLowerCase();
        if (!qs) return apps || [];
        return (apps || []).filter((a) => {
            const c = a.candidate || {};
            const hay = [
                c.name, c.email, c.location, c.current_title, c.current_company,
                ...(c.skills || []),
            ].filter(Boolean).join(" ").toLowerCase();
            return hay.includes(qs);
        });
    }, [apps, q]);

    const grouped = useMemo(() => {
        const map = Object.fromEntries((stages || []).map((s) => [s.key, []]));
        for (const a of filtered) {
            const key = a.stage || "Applied";
            if (!map[key]) map[key] = [];
            map[key].push(a);
        }
        return map;
    }, [filtered, stages]);

    const unfilteredCounts = useMemo(() => {
        const map = Object.fromEntries((stages || []).map((s) => [s.key, 0]));
        for (const a of apps || []) {
            const k = a.stage || "Applied";
            map[k] = (map[k] || 0) + 1;
        }
        return map;
    }, [apps, stages]);

    const activeApp = useMemo(
        () => (apps || []).find((a) => a.id === activeId),
        [apps, activeId]
    );

    const handleDragEnd = (event) => {
        setActiveId(null);
        const { active, over } = event;
        if (!over) return;
        const fromStage = (active.data?.current?.app?.stage) || "Applied";
        const toStage = over.id;
        if (fromStage === toStage) return;
        onStageChange?.(active.id, toStage);
    };

    if (!stages || stages.length === 0) {
        return (
            <div className="border border-dashed border-border p-12 text-center text-sm text-muted-foreground" data-testid="kanban-empty">
                No pipeline stages configured. Set them in Company → Pipeline.
            </div>
        );
    }

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={(e) => { setActiveId(e.active.id); hoverEnd?.(); }}
            onDragCancel={() => setActiveId(null)}
            onDragEnd={handleDragEnd}
        >
            {/* Quick text-search across cards */}
            <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid="kanban-search-bar">
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                    <SearchIcon className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground/40" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search by name, skill, company, location…"
                        className="w-full h-9 pl-8 pr-8 border border-border text-sm bg-white outline-none focus:border-ikb"
                        data-testid="kanban-search"
                    />
                    {q && (
                        <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-foreground/60 hover:text-foreground" aria-label="Clear" data-testid="kanban-search-clear">
                            <X className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
                {q && (
                    <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground" data-testid="kanban-search-count">
                        {filtered.length} / {(apps || []).length} match{filtered.length === 1 ? "" : "es"}
                    </span>
                )}
            </div>
            {bulkSelect && selectedIds.size > 0 && (
                <div className="mb-3 flex items-center gap-2 flex-wrap border-2 border-ikb bg-blue-50/60 px-3 py-2" data-testid="kanban-bulk-bar">
                    <span className="text-[11px] font-mono uppercase tracking-widest font-bold text-ikb">{selectedIds.size} selected</span>
                    <span className="h-4 w-px bg-border" />
                    <select
                        value={bulkStage}
                        onChange={(e) => setBulkStage(e.target.value)}
                        className="h-8 border border-border bg-white px-2 text-[11px] font-mono uppercase tracking-widest outline-none focus:border-ikb"
                        data-testid="kanban-bulk-stage-select"
                    >
                        <option value="">Move to stage…</option>
                        {stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                    </select>
                    <button type="button" disabled={!bulkStage} onClick={() => fireBulk("move", { stage: bulkStage })} className="h-8 px-2.5 text-[11px] font-mono uppercase tracking-widest bg-ikb text-white disabled:opacity-40 hover:bg-[#001f7a]" data-testid="kanban-bulk-move">Move</button>
                    <button type="button" onClick={() => fireBulk("interview")} className="h-8 px-2.5 text-[11px] font-mono uppercase tracking-widest border border-border bg-white hover:border-ikb hover:text-ikb" data-testid="kanban-bulk-interview">Schedule interview</button>
                    <button type="button" onClick={() => fireBulk("email")} className="h-8 px-2.5 text-[11px] font-mono uppercase tracking-widest border border-border bg-white hover:border-ikb hover:text-ikb" data-testid="kanban-bulk-email">Email</button>
                    <button type="button" onClick={() => fireBulk("reject")} className="h-8 px-2.5 text-[11px] font-mono uppercase tracking-widest border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100" data-testid="kanban-bulk-reject">Reject</button>
                    <button type="button" onClick={clearSelection} className="ml-auto h-8 px-2 text-[11px] font-mono uppercase tracking-widest text-foreground/60 hover:text-foreground" data-testid="kanban-bulk-clear">Clear</button>
                </div>
            )}
            <div className="flex gap-3 overflow-x-auto pb-3" data-testid="kanban-board">
                {stages.map((s) => (
                    <Column
                        key={s.key}
                        stage={s}
                        apps={grouped[s.key] || []}
                        onCardClick={onCardClick}
                        onPreviewResume={onPreviewResume}
                        onPreviewProfile={onPreviewProfile}
                        onRetryParse={onRetryParse}
                        onHoverStart={hoverStart}
                        onHoverEnd={hoverEnd}
                        agingHeat={agingHeat}
                        selectable={bulkSelect}
                        selectedIds={selectedIds}
                        onToggleSelect={toggleSelect}
                        totalUnfiltered={unfilteredCounts[s.key]}
                    />
                ))}
            </div>
            {smartHover && hover && <CandidateHoverCard app={hover.app} rect={hover.rect} />}
            <DragOverlay>
                {activeApp ? (
                    <div className="bg-white border-2 border-ikb shadow-lg p-3 w-[244px] cursor-grabbing">
                        <div className="font-medium text-sm truncate">{activeApp.candidate?.name || "Unknown"}</div>
                        <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                            {activeApp.candidate?.current_title || "—"}
                        </div>
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
}
